import {buttonClasses, styled, typographyClasses} from '@mui/material';
import {forwardRef, HTMLAttributes, MouseEventHandler} from 'react';
import {Button, ButtonProps} from '../Button';
import {Typography} from '../Typography';

type PickedButtonProps = Pick<ButtonProps, 'component' | 'href' | 'onClick' | 'startIcon' | 'endIcon' | 'children'>;

export interface NavItemButtonProps extends Partial<PickedButtonProps>, HTMLAttributes<unknown> {
    /**
     * Select state.
     * @default false
     */
    selected?: boolean;
    /**
     * Navigation label. Can show up as a button text or as a sub-navigation menu header
     * @default '';
     */
    label?: string;
    /**
      * On Click Handler
      */
    onClick?: MouseEventHandler<unknown>;
    /**
     * Button orientation. Use 'top' if used within the top navigation, or side if used in the a side navigation variant.
     * @default 'top'
     */
    orientation?: 'side' | 'top';
    /**
     * Adds padding to nested elements
     * @default 0
     */
    level?: number;
}

const StyledButton = styled(Button, {
    shouldForwardProp: (prop) => !['subNavigationItems', 'orientation', 'size', 'level', 'label'].includes(prop as string)
})<NavItemButtonProps>(({
    theme, level, orientation, selected
}) => ({
    // stronger overrides (specificity)
    [`&.${buttonClasses.root}`]: {
        boxSizing: 'border-box',
        boxShadow: 'none !important',

        // align content
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'start',
        position: 'relative',

        textDecoration: 'none',
        color: theme.tokens.FuelColorGray600,
        border: 'unset',
        borderRadius: 'unset',

        cursor: 'pointer',

        [`&.${buttonClasses.focusVisible}`]: {
            zIndex: 99,
            outline: `solid 2px ${theme.tokens.FuelColorFocused}`
        },

        ...orientation === 'top' && {
            height: theme.tokens.FuelSpacing10,
            paddingLeft: theme.tokens.FuelSpacing3,
            paddingRight: theme.tokens.FuelSpacing3,
            maxWidth: '15rem'
        },

        ...orientation === 'side' && {
            display: 'flex',
            width: 'calc(100% - 7px)',
            borderRadius: theme.tokens.FuelBorderRadiusBase,
            marginLeft: '7px', // includes green bar width 3px

            ...level && level > 0 && {
                paddingLeft: `calc(15px + 1.25rem + ${theme.tokens.FuelSpacing3})` // TODO: icon size
            },

            [`& .${typographyClasses.button}`]: {
                maxWidth: '100%',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',

                paddingRight: theme.tokens.FuelSpacing2,
                textAlign: 'left'
            }
        },

        [`& .${buttonClasses.startIcon}`]: {
            ...orientation === 'top' && {
                marginLeft: -theme.tokens.FuelSpacing2, // icon padding
                marginRight: theme.tokens.FuelSpacing2
            },

            // push caret icon all the way to the right
            ...orientation === 'side' && {
                marginLeft: 0, // reset
                marginRight: theme.tokens.FuelSpacing3
            }
        },

        [`& .${buttonClasses.endIcon}`]: {
            ...orientation === 'top' && {
                marginRight: -theme.tokens.FuelSpacing2, // icon padding
                marginLeft: theme.tokens.FuelSpacing1
            },

            // push caret icon all the way to the right
            ...orientation === 'side' && {
                marginLeft: 'auto'
            }
        },

        ...selected && {
            color: theme.tokens.FuelColorTextBrandPrimary,

            ...orientation === 'top' && {
                backgroundColor: 'transparent'
            },

            ...orientation === 'side' && {
                backgroundColor: theme.tokens.FuelColorGreen50
            },

            '&::before': {
                backgroundColor: theme.tokens.FuelColorBrandPrimary,
                display: 'block',
                content: '""',
                position: 'absolute',
                zIndex: 1,

                ...orientation === 'top' && {
                    width: '100%',
                    height: '3px',
                    bottom: 0,
                    left: 0,
                    top: 'auto'
                },

                ...orientation === 'side' && {
                    borderRadius: theme.tokens.FuelBorderRadiusFull,
                    left: '-7px', // green bar width 3px
                    width: '3px',
                    height: '100%'
                }
            }
        }
    }
}));

export const NavItemButton = forwardRef<unknown, NavItemButtonProps>((props, ref) => {
    return (
        <StyledButton
            {...props}
            ref={ref}
        >
            <Typography
                noWrap
                variant='button'
            >
                {props.children}
            </Typography>
        </StyledButton>
    );
});

NavItemButton.defaultProps = {
    orientation: 'top'
};

NavItemButton.displayName = 'NavItemButton';
